import { JSX, ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { asTitle } from "utils/Utils";

import { Field, FieldType, GroupField } from "core/field/FieldService";

export interface InformationSubHeaderProps {
	fields: Field[];
	selection: string[];

	onSelect: (ids: string[]) => void;
}

type SelectionOption = {
	uuid: string;
	index: string;
	name: string;
};

function InformationSubHeader(props: InformationSubHeaderProps): JSX.Element {
	const { t } = useTranslation();

	const root: SelectionOption = {
		uuid: "",
		index: "*",
		name: t("everything")
	};

	const selectionOptions: SelectionOption[] =
		useMemo((): SelectionOption[] => {
			const flattenedList = [root];
			flattenedList.push(...flatFieldList(props.fields));
			return flattenedList;
		}, [props.fields]);

	function flatFieldList(fields: Field[], prefix = ""): SelectionOption[] {
		return fields
			.map((field, index) => {
				const entryIndex = prefix + (index + 1);
				const entries = [
					{
						uuid: field.uuid,
						index: entryIndex,
						name: field.name
					}
				];

				if (field.type === FieldType.GROUP) {
					const group = field as GroupField;
					entries.push(
						...flatFieldList(group.children, entryIndex + ".")
					);
				}

				return entries;
			})
			.flat();
	}

	const handleSelect = useCallback(
		(event: SelectChangeEvent<string[]>): void => {
			const value = event.target.value;
			if (typeof value === "string") {
				props.onSelect(value.split(","));
			} else {
				props.onSelect(value);
			}
		},
		[props.onSelect]
	);

	const renderValue = useCallback((selected: string[]): ReactNode => {
		if (selected.length === 0) {
			return <em>{asTitle(t("selectOneOrMoreParts"))}</em>;
		}

		return selectionOptions
			.filter(field => selected.includes(field.uuid))
			.map(field => `${field.index} ${field.name}`)
			.join(", ");
	}, []);

	return (
		<Grid container alignItems="center">
			<Grid item xs={6} sm={7} md={6} lg={7} />
			<Grid item xs={6} sm={5} md={4} lg={3}>
				<Box sx={{ position: "relative", height: "32px" }}>
					<Select
						variant="standard"
						sx={{ position: "absolute", width: "100%" }}
						multiple
						displayEmpty
						value={props.selection}
						onChange={handleSelect}
						renderValue={renderValue}>
						{selectionOptions.map(field => (
							<MenuItem key={field.uuid} value={field.uuid}>
								{field.index} {field.name}
							</MenuItem>
						))}
					</Select>
				</Box>
			</Grid>
		</Grid>
	);
}

export default InformationSubHeader;
